import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Heart, Trash2, ShoppingCart } from 'lucide-react';
import { wishlistAPI, cartItemsAPI } from '../services/api';
// Uses global header via Layout

const Wishlist = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const res = await wishlistAPI.getAll();
      setItems(res.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleRemove = async (id) => {
    await wishlistAPI.remove(id);
    setItems((prev) => prev.filter((i) => i.id !== id));
  };

  const handleClear = async () => {
    await wishlistAPI.clear();
    setItems([]);
    navigate('/dashboard');
  };

  const moveToCart = async (product) => {
    await cartItemsAPI.add(product.id, 1);
  };

  if (loading) return (
    <div className="bg-gray-50 flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
    </div>
  );

  return (
    <div className="bg-gray-50">
      <div className="max-w-5xl mx-auto px-3 sm:px-4 md:px-6 sm:py-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-8 gap-3 sm:gap-0">
          {items.length > 0 && (
            <button
              onClick={handleClear}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 transition-all duration-150 text-white text-sm sm:text-base font-semibold shadow-md hover:shadow-lg active:scale-95"
            >
              <Trash2 className="w-4 h-4" />
              Clear All
            </button>
          )}
        </div>
        {items.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-8 sm:p-12 text-center">
            <div className="flex flex-col items-center justify-center">
              <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gray-100 rounded-full flex items-center justify-center mb-6">
                <Heart className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">
                Your Wishlist is Empty
              </h2>
              <p className="text-gray-600 text-base sm:text-lg mb-6 sm:mb-8 max-w-md mx-auto">
                There are no items in your wishlist. Start adding products you love to your wishlist!
              </p>
              <Link
                to="/dashboard"
                className="inline-flex items-center justify-center px-6 py-3 sm:px-8 sm:py-4 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white rounded-lg font-semibold text-base sm:text-lg transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                Browse Products
              </Link>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {items.map((item) => (
              <div key={item.id} className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden">
                <div className="relative">
                  <img 
                    src={item.product.image_url} 
                    alt={item.product.name} 
                    className="w-full h-32 sm:h-40 md:h-44 object-cover"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/300x300?text=No+Image';
                    }}
                  />
                </div>
                <div className="p-2.5 sm:p-3 md:p-4">
                  <div className="font-semibold text-sm sm:text-base mb-1 line-clamp-2 min-h-[2.5rem] sm:min-h-[3rem]">
                    {item.product.name}
                  </div>
                  <div className="text-base sm:text-lg font-bold text-green-600 mb-2 sm:mb-3">
                    {item.product.formatted_price}
                  </div>
                  <div className="flex flex-col sm:flex-row gap-1.5 sm:gap-2">
                    <button 
                      onClick={() => moveToCart(item.product)} 
                      className="flex-1 px-2.5 py-1.5 sm:px-3 sm:py-2 bg-green-600 hover:bg-green-700 text-white rounded-full text-xs sm:text-sm font-medium transition-colors flex items-center justify-center gap-1.5"
                    >
                      <ShoppingCart className="w-3.5 h-3.5" />
                      Add to Cart
                    </button>
                    <button 
                      onClick={() => handleRemove(item.id)} 
                      className="px-2.5 py-1.5 sm:px-3 sm:py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-full text-xs sm:text-sm font-medium transition-colors flex items-center justify-center"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Wishlist;


